import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';

import { useAppDispatch, useAppSelector } from '@core/store/hooks';
import { Filename, Spinner } from '@core/components/ui';
import { logger } from '@core/utils';
import { selectCurrentUser } from '@features/auth';
import { selectCurrentProject } from '@features/projects';
import { useSaveAgentFileMutation, dataroomApi } from '@features/dataroom';
import {
  selectCanvasOpen,
  selectCanvasDocument,
  selectCanvasPage,
  selectCanvasNumPages,
  selectChatQuote,
  closeCanvas,
  openCanvas,
  applyCanvasDirective,
  refreshCanvasInPlace,
  clearChatQuote,
} from '@features/workspace';
import {
  useGetMessagesQuery,
  useGetConversationsQuery,
  useCreateConversationMutation,
  useGetAgentStatusQuery,
  useChatTurn,
  findRunningTurn,
  useVoiceInput,
  setActiveTurn,
  setThinkingSteps,
  selectCurrentConversationUuid,
  selectMessages,
  selectIsSending,
  selectCurrentThinkingStep,
  selectThinkingSteps,
  selectHasPendingQuestions,
  selectCurrentQuestion,
  selectPendingQuestions,
  selectError,
  setCurrentConversation,
  startNewConversation,
  setMessages,
  setError,
  clearError,
  clearPendingQuestions,
  ChatMessage,
  ThinkingTimeline,
  UserQuestionCard,
  VoiceInputButton,
  VoiceRecordingBar,
} from '@features/ai';

import {
  buildGeneratedFileCanvasDocument,
  isGeneratedFileSavedDocument,
} from '../utils/generatedFileCanvas';
import { formatCanvasQuote } from '../utils/canvasQuote';

// Label, description and prompt are i18n keys: the prompt lands in the
// composer, so it is written in the user's language too.
const SUGGESTION_CARDS = [
  {
    icon: 'mdi:scale-balance',
    label: 'workspace.chat.suggestions.benchmarkPeers.label',
    description: 'workspace.chat.suggestions.benchmarkPeers.description',
    prompt: 'workspace.chat.suggestions.benchmarkPeers.prompt',
  },
  {
    icon: 'mdi:chart-timeline-variant-shimmer',
    label: 'workspace.chat.suggestions.revenueForecast.label',
    description: 'workspace.chat.suggestions.revenueForecast.description',
    prompt: 'workspace.chat.suggestions.revenueForecast.prompt',
  },
  {
    icon: 'mdi:alert-decagram-outline',
    label: 'workspace.chat.suggestions.pressureTest.label',
    description: 'workspace.chat.suggestions.pressureTest.description',
    prompt: 'workspace.chat.suggestions.pressureTest.prompt',
  },
  {
    icon: 'mdi:clipboard-search-outline',
    label: 'workspace.chat.suggestions.diligencePlan.label',
    description: 'workspace.chat.suggestions.diligencePlan.description',
    prompt: 'workspace.chat.suggestions.diligencePlan.prompt',
  },
  {
    icon: 'mdi:rocket-launch-outline',
    label: 'workspace.chat.suggestions.valueCreation.label',
    description: 'workspace.chat.suggestions.valueCreation.description',
    prompt: 'workspace.chat.suggestions.valueCreation.prompt',
  },
  {
    icon: 'mdi:file-document-check-outline',
    label: 'workspace.chat.suggestions.icPreRead.label',
    description: 'workspace.chat.suggestions.icPreRead.description',
    prompt: 'workspace.chat.suggestions.icPreRead.prompt',
  },
];

const FILE_TYPE_ICONS = {
  '.pdf': 'mdi:file-pdf-box',
  '.docx': 'mdi:file-word-box',
  '.doc': 'mdi:file-word-box',
  '.xlsx': 'mdi:file-excel-box',
  '.xls': 'mdi:file-excel-box',
  '.pptx': 'mdi:file-powerpoint-box',
  '.ppt': 'mdi:file-powerpoint-box',
  '.png': 'mdi:file-image-box',
  '.jpg': 'mdi:file-image-box',
  '.jpeg': 'mdi:file-image-box',
  '.csv': 'mdi:file-delimited-outline',
  '.txt': 'mdi:file-document-outline',
  '.zip': 'mdi:folder-zip',
};

function DocumentPill({ document, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 px-3 py-1 my-2 text-xs text-[var(--color-accent)] bg-[var(--color-accent)]/10 border border-[var(--color-accent)]/20 rounded-full hover:bg-[var(--color-accent)]/20 cursor-pointer transition-colors"
    >
      <Icon icon="mdi:file-document-outline" className="size-3.5" />
      <Filename name={document.name} className="max-w-48" />
      <Icon icon="mdi:dock-window" className="size-3.5 ml-0.5 opacity-60" />
    </button>
  );
}

function GeneratedFilePill({ file, onClick, onSave, isSaved, isSaving }) {
  const { t } = useTranslation();
  const icon = FILE_TYPE_ICONS[file.file_type] || 'mdi:file-outline';
  const isSavedDocument = isGeneratedFileSavedDocument(file);
  const pillClass = isSaved
    ? 'text-blue-700 bg-blue-50 border-blue-200'
    : isSaving
      ? 'text-amber-700 bg-amber-50 border-amber-200'
      : 'text-emerald-700 bg-emerald-50 border-emerald-200';
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 my-1 text-xs border rounded-full transition-colors ${pillClass}`}>
      <button
        type="button"
        onClick={onClick}
        className="inline-flex items-center gap-1.5 cursor-pointer hover:opacity-80 transition-opacity"
      >
        {isSaving ? (
          <Icon icon="mdi:loading" className="size-3.5 animate-spin" />
        ) : (
          <Icon icon={isSaved ? 'mdi:check-circle' : icon} className="size-3.5" />
        )}
        <Filename name={file.filename} className="max-w-48" />
        {isSaved ? (
          <span className="text-[10px] font-medium opacity-70">{t('workspace.chat.saved')}</span>
        ) : isSaving ? (
          <span className="text-[10px] font-medium opacity-70">{t('workspace.chat.saving')}</span>
        ) : (
          <Icon icon="mdi:dock-window" className="size-3.5 ml-0.5 opacity-60" />
        )}
      </button>
      {!isSavedDocument && !isSaved && !isSaving && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onSave(file); }}
          title={t('workspace.chat.saveToFiles')}
          className="cursor-pointer p-0.5 rounded hover:bg-emerald-200 transition-colors"
        >
          <Icon icon="mdi:plus" className="size-3.5" />
        </button>
      )}
    </span>
  );
}

function EmptyChat({ agentName, onSuggestionClick }) {
  const { t } = useTranslation();
  return (
    <div className="flex-1 flex items-center justify-center px-6 py-10">
      <div className="w-full max-w-3xl">
        <div className="text-center mb-8">
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-[var(--color-accent)] mb-2">
            {t('workspace.chat.empty.eyebrow')}
          </p>
          <h2 className="text-2xl font-semibold text-[var(--color-text)] mb-2">
            {t('workspace.chat.empty.title')}
          </h2>
          <p className="text-sm text-[var(--color-text-muted)] max-w-xl mx-auto">
            {t('workspace.chat.empty.body', { agentName })}
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {SUGGESTION_CARDS.map((card) => (
            <button
              key={card.label}
              onClick={() => onSuggestionClick?.(t(card.prompt))}
              className="group flex min-h-32 flex-col items-start p-4 text-left border border-[var(--color-border)] rounded-xl bg-white hover:border-[var(--color-accent)] hover:shadow-sm cursor-pointer transition-all"
            >
              <span className="flex size-9 items-center justify-center rounded-lg bg-[var(--color-accent)]/10 mb-4 group-hover:bg-[var(--color-accent)] group-hover:text-white text-[var(--color-accent)] transition-colors">
                <Icon icon={card.icon} className="size-5" />
              </span>
              <span className="text-sm font-semibold text-[var(--color-text)]">{t(card.label)}</span>
              <span className="text-xs leading-5 text-[var(--color-text-muted)] mt-1">{t(card.description)}</span>
            </button>
          ))}
        </div>
        <p className="text-center text-xs text-[var(--color-text-muted)] mt-5">
          {t('workspace.chat.empty.footer')}
        </p>
      </div>
    </div>
  );
}

export function ChatPanel() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const [inputValue, setInputValue] = useState('');
  const [savedFiles, setSavedFiles] = useState(new Set());
  const [savingFiles, setSavingFiles] = useState(new Set());

  const currentProject = useAppSelector(selectCurrentProject);
  const projectUuid = currentProject?.uuid || undefined;
  const [saveAgentFile] = useSaveAgentFileMutation();

  const { data: agentStatus } = useGetAgentStatusQuery();
  const agentName = agentStatus?.agent_name || 'Neovision Platform';

  const conversationUuid = useAppSelector(selectCurrentConversationUuid);
  const messages = useAppSelector(selectMessages);
  const isSending = useAppSelector(selectIsSending);
  const currentThinkingStep = useAppSelector(selectCurrentThinkingStep);
  const thinkingSteps = useAppSelector(selectThinkingSteps);
  const hasPendingQuestions = useAppSelector(selectHasPendingQuestions);
  const currentQuestion = useAppSelector(selectCurrentQuestion);
  const pendingQuestions = useAppSelector(selectPendingQuestions);
  const error = useAppSelector(selectError);

  const canvasOpen = useAppSelector(selectCanvasOpen);
  const canvasDocument = useAppSelector(selectCanvasDocument);
  const canvasPage = useAppSelector(selectCanvasPage);
  const canvasNumPages = useAppSelector(selectCanvasNumPages);
  const chatQuote = useAppSelector(selectChatQuote);
  const canvasDocumentUuid = canvasOpen && canvasDocument ? canvasDocument.uuid : null;
  const isPdfCanvas = canvasDocumentUuid && canvasDocument?.file_type === '.pdf';
  // `currentData`, never `data`: RTK Query keeps `data` pointing at the
  // PREVIOUS arg's result across arg changes and across `skip`. Since the
  // effect below only refills Redux when this identity changes, re-opening a
  // still-cached conversation would hand back the same array and leave the
  // chat permanently empty while the title (read from the conversation list)
  // rendered fine.
  const { currentData: apiMessages, isFetching: isFetchingMessages } = useGetMessagesQuery(
    conversationUuid,
    { skip: !conversationUuid },
  );

  const [createConversation] = useCreateConversationMutation();

  // What happens to the canvas once a turn lands: an explicit agent directive
  // (open/refresh a report) wins over the generated-file auto-open.
  const onTurnSettled = useCallback(({ row, ranTools }) => {
    if (ranTools.has('edit_report_source')) dispatch(refreshCanvasInPlace());
    const directive = row.canvas_directive;
    if (directive?.document_uuid) {
      dispatch(
        applyCanvasDirective({
          document: {
            uuid: directive.document_uuid,
            name: directive.name,
            file_type: directive.file_type,
            project_uuid: directive.project_uuid,
            source_document_uuid: directive.source_document ?? null,
            isAgentFile: false,
          },
          page: directive.page ?? null,
        }),
      );
      return;
    }
    const files = row.generated_files || [];
    const imageExts = new Set(['.png', '.jpg', '.jpeg', '.svg', '.webp']);
    const docFile = files.find((f) => !imageExts.has(f.file_type));
    if (docFile) {
      dispatch(openCanvas(buildGeneratedFileCanvasDocument(docFile, projectUuid || null)));
    }
  }, [dispatch, projectUuid]);
  const { sendTurn, cancel: cancelTurn } = useChatTurn(conversationUuid, { onSettled: onTurnSettled });

  // Voice input — transcribed text goes into the input field
  const handleTranscription = useCallback((text) => {
    setInputValue((prev) => (prev ? `${prev} ${text}` : text));
    inputRef.current?.focus();
  }, []);
  const {
    isRecording,
    isTranscribing,
    error: voiceError,
    analyserRef,
    startRecording,
    stopAndTranscribe,
    cancelRecording,
  } = useVoiceInput({ onTranscription: handleTranscription, projectUuid });

  // Close canvas when switching between conversations (not on first creation from null)
  const prevConversationRef = useRef(conversationUuid);
  useEffect(() => {
    if (prevConversationRef.current !== conversationUuid) {
      const wasExistingConversation = prevConversationRef.current != null;
      prevConversationRef.current = conversationUuid;
      if (wasExistingConversation && canvasOpen) {
        dispatch(closeCanvas());
      }
    }
  }, [conversationUuid, canvasOpen, dispatch]);

  // Reset conversation when project changes
  const prevProjectRef = useRef(projectUuid);
  useEffect(() => {
    if (prevProjectRef.current !== projectUuid) {
      prevProjectRef.current = projectUuid;
      dispatch(startNewConversation());
    }
  }, [projectUuid, dispatch]);

  // Sync API messages to Redux. Keyed on the conversation too, so a selection
  // that clears `messages` is always followed by a refill.
  useEffect(() => {
    if (apiMessages && !isSending) {
      dispatch(setMessages(apiMessages));
      // A turn still running on the server (this tab reloaded, or came back
      // to the conversation) is adopted so polling resumes where it was.
      const running = findRunningTurn(apiMessages);
      if (running && conversationUuid) {
        dispatch(setThinkingSteps(running.thinking_steps || []));
        dispatch(setActiveTurn({ conversationUuid, messageUuid: running.uuid }));
      }
    }
  }, [apiMessages, conversationUuid, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, thinkingSteps.length, hasPendingQuestions]);

  // Seed a canvas text selection into the composer as a quoted snippet.
  useEffect(() => {
    if (!chatQuote) return;
    const snippet = formatCanvasQuote(chatQuote, t);
    setInputValue((prev) => (prev ? `${prev}\n${snippet}` : snippet));
    inputRef.current?.focus();
    dispatch(clearChatQuote());
  }, [chatQuote, dispatch, t]);

  const sendMessageContent = useCallback(async (content, activeConversationUuid, { canvasDocumentUuid: docUuid, canvasPage: page } = {}) => {
    try {
      await sendTurn(content, activeConversationUuid, { canvasDocumentUuid: docUuid, canvasPage: page });
    } catch (err) {
      logger.error('Failed to start the reply:', err);
    }
  }, [sendTurn]);

  const handleSendMessage = useCallback(async () => {
    if (!inputValue.trim() || isSending) return;

    // A free message while the agent's question card is open is the answer
    // to it (the server records it as the "Other" answer); the card must go.
    if (hasPendingQuestions) dispatch(clearPendingQuestions());

    const messageContent = inputValue.trim();
    setInputValue('');
    inputRef.current?.focus();

    try {
      let activeConversationUuid = conversationUuid;

      if (!activeConversationUuid) {
        const result = await createConversation(projectUuid).unwrap();
        activeConversationUuid = result.uuid;
        dispatch(setCurrentConversation(activeConversationUuid));
        navigate(`/chat/${result.short_id}`, { replace: true });
      }

      await sendMessageContent(messageContent, activeConversationUuid, {
        canvasDocumentUuid: canvasDocumentUuid || undefined,
        canvasPage: canvasPage || undefined,
      });
    } catch (err) {
      logger.error('Failed to create conversation:', err);
      dispatch(setError(err.message || t('workspace.chat.createConversationFailed')));
    }
  }, [inputValue, isSending, hasPendingQuestions, conversationUuid, projectUuid, canvasDocumentUuid, canvasPage, createConversation, sendMessageContent, dispatch, navigate, t]);

  const handleKeyPress = useCallback(
    (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSendMessage();
      }
    },
    [handleSendMessage],
  );

  // Send a synthetic user message after an inline `upload://<uuid>` widget
  // completes uploading the file, so the agent can pick up where it left off.
  const handleUploadComplete = useCallback(
    async (uploadTokenUuid, filename) => {
      if (!conversationUuid) return;
      const note = filename
        ? `I uploaded ${filename}. upload_token_uuid=${uploadTokenUuid}. Please proceed.`
        : `Upload complete. upload_token_uuid=${uploadTokenUuid}. Please proceed.`;
      await sendMessageContent(note, conversationUuid);
    },
    [conversationUuid, sendMessageContent],
  );

  const handleQuestionReply = useCallback(
    async (text) => {
      if (!pendingQuestions || isSending) return;
      dispatch(clearPendingQuestions());

      try {
        let activeConversationUuid = conversationUuid;
        if (!activeConversationUuid) {
          const result = await createConversation(projectUuid).unwrap();
          activeConversationUuid = result.uuid;
          dispatch(setCurrentConversation(activeConversationUuid));
          navigate(`/chat/${result.short_id}`, { replace: true });
        }
        await sendMessageContent(text, activeConversationUuid, {
          canvasDocumentUuid: canvasDocumentUuid || undefined,
          canvasPage: canvasPage || undefined,
        });
      } catch (err) {
        logger.error('Failed to send question reply:', err);
        dispatch(setError(err.message || t('workspace.chat.sendReplyFailed')));
      }
    },
    [pendingQuestions, isSending, conversationUuid, projectUuid, canvasDocumentUuid, canvasPage, createConversation, sendMessageContent, dispatch, navigate, t],
  );

  const handleOpenCanvasFromPill = useCallback((doc) => {
    dispatch(openCanvas(doc));
  }, [dispatch]);

  const handleSaveToFiles = useCallback(async (file) => {
    setSavingFiles((prev) => { const next = new Set(prev); next.add(file.filename); return next; });
    try {
      await saveAgentFile({
        projectUuid: currentProject?.uuid || null,
        sourceUrl: file.url,
        filename: file.filename,
      }).unwrap();
      setSavedFiles((prev) => { const next = new Set(prev); next.add(file.filename); return next; });
      dispatch(dataroomApi.util.invalidateTags(['Folder', 'Document']));
    } catch (err) {
      logger.error('Failed to save file to dataroom', err);
    } finally {
      setSavingFiles((prev) => { const next = new Set(prev); next.delete(file.filename); return next; });
    }
  }, [saveAgentFile, currentProject?.uuid]);

  // Handle clicking a suggestion card — pre-fill input and focus
  const handleSuggestionClick = useCallback((prompt) => {
    setInputValue(prompt);
    inputRef.current?.focus();
  }, []);

  const hasMessages = messages && messages.length > 0;

  // Breadcrumb data
  const { currentData: conversations = [] } = useGetConversationsQuery(projectUuid, { skip: !projectUuid });
  const currentConversation = conversationUuid
    ? conversations.find((c) => c.uuid === conversationUuid)
    : null;
  const projectLabel = currentProject?.name;
  const conversationTitle = currentConversation?.first_message;
  // An impersonating superuser can open any member's conversation, but only
  // write into their own: the backend answers 404 to a turn anywhere else.
  const currentUser = useAppSelector(selectCurrentUser);
  const foreignOwner = currentConversation && currentConversation.owner_email !== currentUser?.email
    ? currentConversation.owner_email
    : null;

  return (
    <div className="flex-1 flex flex-col min-w-0" data-tour="workspace-chat">
      {/* Breadcrumb bar */}
      {conversationUuid && currentConversation && (
        <div className="h-10 flex items-center gap-1.5 px-4 border-b border-[var(--color-border)] bg-white shrink-0 text-sm">
          <span className="text-[var(--color-text-muted)]">{projectLabel}</span>
          {conversationTitle && (
            <>
              <Icon icon="mdi:chevron-right" className="size-4 text-[var(--color-text-muted)]" />
              <span className="text-[var(--color-text)] truncate max-w-md">{conversationTitle}</span>
            </>
          )}
          <Icon icon="mdi:chevron-down" className="size-3.5 text-[var(--color-text-muted)] ml-1" />
        </div>
      )}

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto px-4 bg-[var(--color-bg)]">
        {isFetchingMessages && !hasMessages ? (
          <div className="flex-1 flex items-center justify-center h-full">
            <Spinner size="lg" />
          </div>
        ) : hasMessages ? (
          <div className="divide-y divide-[var(--color-border)]">
            {messages.map((message) => {
              const isTurnRunning = message.role === 'assistant' && ['pending', 'running'].includes(message.status);
              // A cancelled or failed turn keeps an empty assistant row in the
              // database; say so instead of rendering an empty bubble. It reads
              // the same after a reload, because the state is on the row.
              if (message.role === 'assistant' && !message.content && ['cancelled', 'failed'].includes(message.status)) {
                return (
                  <p key={message.uuid} className="px-2 py-3 text-xs italic text-[var(--color-text-muted)]" data-testid="turn-note">
                    {message.status === 'cancelled' ? t('workspace.chat.responseCancelled') : message.error || t('workspace.chat.agentCouldNotRespond')}
                  </p>
                );
              }
              return (
              <div key={message.uuid}>
                {message.role === 'user' && message.canvas_document && !canvasOpen && (
                  <DocumentPill
                    document={message.canvas_document}
                    onClick={() => handleOpenCanvasFromPill({
                      ...message.canvas_document,
                      project_uuid: message.canvas_document.project_uuid || message.project_uuid || null,
                    })}
                  />
                )}
                <ChatMessage
                  message={isTurnRunning ? { ...message, isLoading: true } : message}
                  agentName={agentName}
                  onUploadComplete={handleUploadComplete}
                />
                {message.role === 'assistant' && message.generated_files?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 pl-10">
                    {message.generated_files.map((file) => (
                      <GeneratedFilePill
                        key={file.filename}
                        file={file}
                        onClick={() => handleOpenCanvasFromPill(
                          buildGeneratedFileCanvasDocument(file, message.project_uuid || projectUuid || null),
                        )}
                        onSave={handleSaveToFiles}
                        isSaving={savingFiles.has(file.filename)}
                        isSaved={savedFiles.has(file.filename)}
                      />
                    ))}
                  </div>
                )}
              </div>
              );
            })}

            {isSending && thinkingSteps.length > 0 && !hasPendingQuestions && (
              <ThinkingTimeline
                steps={thinkingSteps.map((step) => ({
                  tool: step.tool,
                  query: step.args?.query || step.args?.url || step.args?.document_uuid || null,
                  detail: step.args?.instructions || null,
                  status: step.tool === currentThinkingStep ? 'active' : 'completed',
                }))}
                isStreaming={true}
                isCollapsible={false}
              />
            )}

            {hasPendingQuestions && currentQuestion && (
              <UserQuestionCard
                question={currentQuestion}
                onSubmit={handleQuestionReply}
              />
            )}

            {error && (
              <div className="flex items-center gap-2 mx-2 my-3 px-4 py-3 bg-red-50 border border-red-200 rounded-lg">
                <Icon icon="mdi:alert-circle" className="size-5 text-red-500 flex-shrink-0" />
                <p className="text-sm text-red-700 flex-1">{error}</p>
                <button
                  onClick={() => dispatch(clearError())}
                  className="p-1 text-red-400 hover:text-red-600 cursor-pointer"
                  aria-label={t('workspace.chat.dismissError')}
                >
                  <Icon icon="mdi:close" className="size-4" />
                </button>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        ) : (
          <EmptyChat agentName={agentName} onSuggestionClick={handleSuggestionClick} />
        )}
      </div>

      {foreignOwner ? (
        <div className="border-t border-[var(--color-border)] p-4 bg-[var(--color-bg)] flex-shrink-0" data-testid="foreign-conversation-note">
          <p className="flex items-center gap-2 text-xs text-[var(--color-text-secondary)]">
            <Icon icon="mdi:eye-outline" className="size-4 shrink-0" />
            {t('workspace.chat.readOnlyForeign', { owner: foreignOwner })}
          </p>
        </div>
      ) : (
      /* Input area */
      <div className="border-t border-[var(--color-border)] p-4 bg-[var(--color-bg)] flex-shrink-0" data-tour="chat-composer">
        <div className="flex flex-col border border-[var(--color-border)] rounded-lg focus-within:border-[var(--color-accent)] focus-within:ring-1 focus-within:ring-[var(--color-accent)] transition-colors">
          {canvasDocumentUuid && canvasDocument && (
            <div className="flex items-center gap-2 px-3 py-1.5 border-b border-[var(--color-border)] w-full">
              <Icon
                icon={isPdfCanvas && canvasPage ? 'mdi:book-open-page-variant-outline' : 'mdi:file-document-outline'}
                className="size-4 text-[var(--color-accent)] shrink-0"
              />
              <span className="text-xs text-[var(--color-text-secondary)] truncate">
                {t('workspace.chat.workingWith', { name: canvasDocument.name })}
                {isPdfCanvas && canvasPage
                  ? ` · ${canvasNumPages
                    ? t('workspace.chat.pageOf', { page: canvasPage, total: canvasNumPages })
                    : t('workspace.chat.page', { page: canvasPage })}`
                  : ''}
              </span>
            </div>
          )}

          {isRecording || isTranscribing ? (
            <VoiceRecordingBar
              analyserRef={analyserRef}
              isTranscribing={isTranscribing}
              onConfirm={stopAndTranscribe}
              onCancel={cancelRecording}
            />
          ) : (
            <div className="flex items-end">
              <textarea
                ref={inputRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyPress}
                placeholder={
                  hasPendingQuestions
                    ? t('workspace.chat.placeholderAnswerAbove')
                    : canvasDocumentUuid
                      ? t('workspace.chat.placeholderAskDocument')
                      : t('workspace.chat.placeholderDefault')
                }
                rows={1}
                className="flex-1 px-4 py-3 bg-transparent border-none resize-none focus:outline-none text-sm text-[var(--color-text)] placeholder-[var(--color-text-muted)]"
                style={{ minHeight: '48px', maxHeight: '120px' }}
                disabled={isSending}
              />
              <VoiceInputButton
                error={voiceError}
                disabled={isSending}
                onClick={startRecording}
              />
              {/* While a reply runs the same control stops it: the turn lives on
                  the server, so this is the only way to end it on purpose. */}
              <button
                onClick={isSending ? cancelTurn : handleSendMessage}
                disabled={isSending ? false : !inputValue.trim()}
                className="flex items-center justify-center size-10 m-1 rounded-lg text-[var(--color-accent)] hover:bg-[var(--color-bg-muted)] disabled:text-[var(--color-text-muted)] disabled:hover:bg-transparent disabled:cursor-not-allowed cursor-pointer transition-colors"
                aria-label={isSending ? t('workspace.chat.stopGenerating') : t('workspace.chat.sendMessage')}
                title={isSending ? t('workspace.chat.stop') : undefined}
                data-testid="chat-send-button"
              >
                {isSending ? (
                  <Icon icon="mdi:stop" className="size-5" />
                ) : (
                  <Icon icon="mdi:send" className="size-5" />
                )}
              </button>
            </div>
          )}
        </div>
      </div>
      )}

    </div>
  );
}
